import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const projects = [
  {
    title: "NeuralNexus Core",
    category: "AI Infrastructure",
    description: "Architected a custom LLM orchestration layer for a Fortune 500 fintech firm, reducing inference latency by 40% while maintaining strict data sovereignty.",
    tags: ["Python", "PyTorch", "Kubernetes", "Vector DBs"],
    stats: { metric: "40%", label: "Latency Reduction" },
    image: "https://images.unsplash.com/photo-1639322537228-f710d846310a?q=80&w=2000&auto=format&fit=crop"
  },
  {
    title: "QuantumLedger",
    category: "Distributed Systems",
    description: "Developed a high-throughput transaction engine capable of processing 100k+ TPS with deterministic finality for a decentralized finance protocol.",
    tags: ["Rust", "gRPC", "PostgreSQL", "Wasm"],
    stats: { metric: "100k+", label: "TPS Capacity" },
    image: "https://images.unsplash.com/photo-1639762681485-074b7f938ba0?q=80&w=2000&auto=format&fit=crop"
  },
  {
    title: "AeroStream IoT",
    category: "Real-time Data",
    description: "Engineered a global sensor mesh network backend that ingests and analyzes 5TB of telemetry data daily with sub-second processing windows.",
    tags: ["Go", "Kafka", "ClickHouse", "MQTT"],
    stats: { metric: "5TB/day", label: "Data Throughput" },
    image: "https://images.unsplash.com/photo-1451187580459-43490279c0fa?q=80&w=2000&auto=format&fit=crop"
  }
];

const CaseStudies = () => {
  return (
    <section id="work" className="py-32 bg-black relative overflow-hidden">
      {/* Background Glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[1000px] h-[600px] bg-cyan-900/5 blur-[120px] rounded-full pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        <div className="flex flex-col md:flex-row justify-between items-end mb-20 gap-8">
          <div className="max-w-2xl">
            <motion.h2 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-4xl md:text-6xl font-bold tracking-tighter mb-6"
            >
              Selected <span className="text-zinc-500">Deployments.</span>
            </motion.h2>
            <p className="text-zinc-400 text-lg font-light">
              A glimpse into the complex systems we've architected and deployed for industry leaders.
            </p>
          </div>
          <div className="hidden md:block">
            <div className="flex gap-2">
              <div className="w-12 h-12 rounded-full border border-zinc-800 flex items-center justify-center text-zinc-500">
                <SafeIcon icon={FiIcons.FiArrowLeft} />
              </div>
              <div className="w-12 h-12 rounded-full border border-zinc-800 flex items-center justify-center text-cyan-500">
                <SafeIcon icon={FiIcons.FiArrowRight} />
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {projects.map((project, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: idx * 0.1 }}
              className="group relative flex flex-col bg-zinc-950 border border-zinc-900 rounded-2xl overflow-hidden hover:border-cyan-500/30 transition-all duration-500"
            >
              {/* Image Container */}
              <div className="aspect-[16/10] overflow-hidden relative">
                <img 
                  src={project.image} 
                  alt={project.title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110 grayscale group-hover:grayscale-0 opacity-50 group-hover:opacity-80"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-transparent to-transparent" />
                
                {/* Stat Badge */}
                <div className="absolute top-4 right-4 bg-black/60 backdrop-blur-md border border-white/10 px-3 py-1.5 rounded-lg">
                  <div className="text-cyan-400 font-mono text-sm font-bold">{project.stats.metric}</div>
                  <div className="text-[8px] uppercase tracking-widest text-zinc-500">{project.stats.label}</div>
                </div>
              </div>

              {/* Content */}
              <div className="p-8 flex flex-col flex-grow">
                <div className="flex items-center gap-2 mb-4">
                  <span className="w-1.5 h-1.5 rounded-full bg-cyan-500" />
                  <span className="text-xs font-mono text-zinc-500 uppercase tracking-widest">{project.category}</span>
                </div>
                
                <h3 className="text-2xl font-bold mb-4 group-hover:text-white transition-colors">{project.title}</h3>
                <p className="text-zinc-500 text-sm font-light leading-relaxed mb-6 flex-grow">
                  {project.description}
                </p>

                {/* Tech Tags */}
                <div className="flex flex-wrap gap-2 pt-6 border-t border-zinc-900">
                  {project.tags.map((tag, i) => (
                    <span 
                      key={i} 
                      className="text-[10px] font-mono px-2 py-1 bg-zinc-900 text-zinc-400 rounded border border-zinc-800"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>

              {/* Hover Action */}
              <div className="absolute bottom-4 right-4 opacity-0 group-hover:opacity-100 translate-x-2 group-hover:translate-x-0 transition-all">
                <div className="w-10 h-10 rounded-full bg-cyan-500 text-black flex items-center justify-center">
                  <SafeIcon icon={FiIcons.FiArrowUpRight} />
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        <div className="mt-20 text-center">
          <button className="inline-flex items-center gap-2 text-zinc-500 hover:text-cyan-400 transition-colors text-sm font-medium">
            View Technical Archive <SafeIcon icon={FiIcons.FiExternalLink} />
          </button>
        </div>
      </div>
    </section>
  );
};

export default CaseStudies;